import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  FormControl,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { pdf } from "@react-pdf/renderer";

import {
  GetTaxReportHelper,
  GetTaxReportRegimeCalculationHelper,
} from "../../helper/TaxDeclarationHelper";
import { currentMonth, getUserData } from "../../../Base/helper/baseFunctions";
import TaxReport from "./TaxReport";

const MyTaxReport = () => {
  const currentDate = new Date();
  const userData = getUserData();
    const currentYear = currentDate.getFullYear();
    const toYear = currentYear + 1;
    const fromYear = currentYear;
    const dojDate = new Date(userData?.doj);
    const dojYear = dojDate.getFullYear();
    const dojMonth = dojDate.getMonth() + 1; 
    const startYear = dojMonth <= 3 ? dojYear - 1 : dojYear;
  const [financialYear, setFinancialYear] = useState(`${fromYear}-${toYear}`);
  const [isLoading1, setIsLoading1] = useState(false);
  const [pdfMeta, setPdfMeta] = useState({
    url: "",
    fileName: "",
  });
  const years = Array.from({ length: currentYear - startYear + 1 },(_, i) => startYear + i);
 useEffect(() => {
  const loadInitialPdf = async () => {
    setIsLoading1(true);
    await handleDownloadPDF(financialYear);
    setIsLoading1(false);
  };

  loadInitialPdf();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const handleDownloadPDF = async (financialYear) => {
    setIsLoading1(true);
    try {
      const response = await GetTaxReportHelper({
        emp_id: userData?.employee_id,
        fromYear: financialYear.split("-")[0],
        toYear: financialYear.split("-")[1],
        month: currentMonth(),
      });
      if (response?.data?.statuscode === 200 && response?.data?.data?.[0]) {
        const data = response?.data?.data?.[0];
        const financialYearMonths = [
          "April", "May", "June", "July", "August", "September",
          "October", "November", "December", "January", "February", "March"
        ];
      const actualMonthsFromData = Object.keys(data?.actuals || {});
      const projectedMonthsFromData = Object.keys(data?.projected || {});

      const sortMonths = (months) =>
        months?.sort(
          (a, b) =>
            financialYearMonths.indexOf(a) - financialYearMonths.indexOf(b)
        );
      const sortedProjected = sortMonths([...projectedMonthsFromData]);

      // Find the first projected month (e.g. "July")
      const firstProjectedMonth = sortedProjected[0];
      const firstProjectedIndex = financialYearMonths.indexOf(firstProjectedMonth);

      const paddedActualMonths = financialYearMonths
        .slice(0, firstProjectedIndex)
        .concat(actualMonthsFromData);

      // Remove duplicates and sort
      const actualMonths = sortMonths([...new Set(paddedActualMonths)]);
      const projectedMonths = sortMonths(projectedMonthsFromData);

        const allMonths = [...actualMonths, ...projectedMonths];
        const checkRegimeOpted = () => {
          const employeeInfo = data?.employee_info?.[0];
          if (!employeeInfo) return null;
          const regime =
            employeeInfo.regime_opted === "old_regime" ? "OLD" : "NEW";
          return regime;
        };
        const result = {
          earnings: {},
          deductions: {},
        };

        allMonths?.forEach((month) => {
          ["actuals", "projected"].forEach((category) => {
            const monthData = data[category]?.[month];

            if (monthData) {
              const earnings = Array.isArray(monthData.earnings)
                ? monthData.earnings[0]
                : monthData.earnings;
              const deductions = Array.isArray(monthData.deductions)
                ? monthData.deductions[0]
                : monthData.deductions;

              // Process earnings
              if (earnings) {
                Object.entries(earnings).forEach(([component, value]) => {
                  if (!result?.earnings[component]) {
                    result.earnings[component] = {};
                  }
                  result.earnings[component][month] = Number(value) || 0;
                });
              }

              // Process deductions
              if (deductions) {
                Object.entries(deductions).forEach(([component, value]) => {
                  if (!result?.deductions[component]) {
                    result.deductions[component] = {};
                  }
                  result.deductions[component][month] = Number(value) || 0;
                });
              }
            }
          });
        });

      // Process employee_post_adjustment_component_earning_json
        if (Array.isArray(data?.employee_post_adjustment_component_earning_json)) {
          const flatObject = {};
          data?.employee_post_adjustment_component_earning_json?.forEach((component) => {
            const { label, amount, taxable_flag, post_adjustment_components_id } = component;
            if (taxable_flag) {
              const uniqueKey = `${post_adjustment_components_id}_${label}`;
              const value = parseFloat(amount || 0);

              if (flatObject[uniqueKey]) {
                flatObject[uniqueKey] += value;
              } else {
                flatObject[uniqueKey] = value;
              }
            }
          });

          Object.entries(flatObject)?.forEach(([key, totalAmount]) => {
            const label = key.split("_").slice(1).join("_");
            if (!result?.earnings[label]) {
              result.earnings[label] = {};
            }

            allMonths?.forEach((month) => {
              if (actualMonths?.includes(month)) {
                result.earnings[label][month] = (result.earnings[label][month] || 0) + totalAmount;
              } else if (projectedMonths?.includes(month)) {
                result.earnings[label][month] = 0; 
              }
            });
          });
        }
       // Process employee_post_adjustment_component_deduction_json
        if (Array.isArray(data?.employee_post_adjustment_component_deduction_json)) {
          const flatObject = {};
          data?.employee_post_adjustment_component_deduction_json?.forEach((component) => {
            const { label, amount, post_adjustment_components_id } = component;
            const uniqueKey = `${post_adjustment_components_id}_${label}`;
            const value = parseFloat(amount || 0);

            if (flatObject[uniqueKey]) {
              flatObject[uniqueKey] += value;
            } else {
              flatObject[uniqueKey] = value;
            }
          });

          Object.entries(flatObject)?.forEach(([key, totalAmount]) => {
            const label = key.split("_").slice(1).join("_"); 
            if (!result?.deductions[label]) {
              result.deductions[label] = {};
            }

            allMonths?.forEach((month) => {
              if (actualMonths?.includes(month)) {
                result.deductions[label][month] = (result.deductions[label][month] || 0) + totalAmount;
              } else if (projectedMonths?.includes(month)) {
                result.deductions[label][month] = 0; 
              }
            });
          });
        }

        // Filter out components with all fields 0 or null for earnings
        Object.keys(result.earnings).forEach((component) => {
          const isAllZeroOrNull = allMonths.every(
            (month) => !result.earnings[component][month] || result.earnings[component][month] === 0
          );
          if (isAllZeroOrNull) {
            delete result.earnings[component];
          }
        });
        
        // Filter out components with all fields 0 or null for deductions
        Object.keys(result.deductions).forEach((component) => {
          const isAllZeroOrNull = allMonths.every(
            (month) => !result.deductions[component][month] || result.deductions[component][month] === 0
          );
          if (isAllZeroOrNull) {
            delete result.deductions[component];
          }
        });
        // Overall Total Calculator
        const calculateOverallTotal = (data) => {
          return Object.keys(data).reduce((grandTotal, component) => {
            return (
              grandTotal +
              allMonths.reduce((total, month) => {
                return total + Number(data?.[component]?.[month] || 0);
              }, 0)
            );
          }, 0);
        };
        const totalEarnings = calculateOverallTotal(result?.earnings);
        const netSalary =
          Number(totalEarnings) -
          (Number(data?.tax_summary?.profession_tax) +
            Number(data?.tax_summary?.standard_deduction));

        // section show based on regimeSelected
        const declarations = data?.proofs_received || {};
        const sectionSummary = Object.entries(declarations)
          .filter(
            ([section]) => checkRegimeOpted() === "OLD" || section === "Section 24"
          )
          .map(([section, entries]) => {
             let total = 0;
            if (["House Rent Exemption", "Section 24"].includes(section)) {
              const safeEntries = Array.isArray(entries) ? entries : [];
              total = safeEntries?.reduce((acc, obj) => {
                const sum = Object.values(obj).reduce(
                  (s, val) => s + Number(val || 0),
                  0
                );
                return acc + sum;
              }, 0);
            } else {
              const components = entries?.[0] || {};
              total = Object.values(components).reduce(
                (sum, val) => sum + Number(val || 0),
                0
              );
            }
            return {
              section,
              total,
            };
          });
        const allSumSection = sectionSummary.reduce(
          (sum, item) => sum + Number(item.total || 0),
          0
        );
        const taxableIncome = Number(netSalary) - Number(allSumSection);
        const roundedIncome = Math.round((+taxableIncome + Number.EPSILON) * 100) / 100;

        const res = await GetTaxReportRegimeCalculationHelper({
          taxable_income: roundedIncome,
          regime_type: data?.employee_info?.[0]?.regime_opted,
        });

        if (res?.data?.statuscode === 200) {
          const MyDocument = (
            <TaxReport
              data={data}
              slabSplitData={res?.data?.data?.[0]}
              actualMonths={actualMonths}
              projectedMonths={projectedMonths}
              allMonths={allMonths}
              checkRegimeOpted={checkRegimeOpted}
              result={result}
              calculateOverallTotal={calculateOverallTotal}
              totalEarnings={totalEarnings}
              allSumSection={allSumSection}
              sectionSummary={sectionSummary}
              taxableIncome={taxableIncome}
              companyName={userData?.company_full_name}
              fromYear= {financialYear.split("-")[0]}
              toYear= {financialYear.split("-")[1]}
            />
          );
          const blob = await pdf(MyDocument).toBlob();
          const fileNameReport = `TaxReport_${data?.employee_info?.[0]?.employee_no}_FY_${financialYear}.pdf` 
          const url = URL.createObjectURL(blob);
          setPdfMeta({
            url,
            fileName: fileNameReport
          });
        }
      }
    } catch (error) {
      console.error("Error generating the PDF:", error);
    } finally {
      setIsLoading1(false);
    }
  };

  const handleFinancialYearChange = (date) => {
    if (date) {
      setFinancialYear(date);
      handleDownloadPDF(date)
    } else {
      setFinancialYear(null);
    }
  };
  return (
      <Box sx={{ maxHeight: "89vh", overflowY: "auto" }}>
        <Box sx={{ m: 1 }}>
          <Grid
            sx={{ display: "flex", justifyContent: "space-between" }}
            container
            columnSpacing={1}
            mb={1}
          >
            <FormControl sx={{
              minWidth: 160, 
              maxWidth: 200,
              
            }}>
            <InputLabel >Select Financial Year</InputLabel>
            <Select
              value={financialYear || ""}
              label="Select Financial Year"
              onChange={(e) => handleFinancialYearChange(e.target.value)}
              sx={{
                height: 40,
                display: "flex",
                alignItems: "center",
              }}
            >
              {years.map((year) => (
                <MenuItem key={year} value={`${year}-${year + 1}`}>
                  {`${year}-${year + 1}`}
                </MenuItem>
              ))}
            </Select>
            </FormControl>

            { (pdfMeta?.url  && !isLoading1) &&  <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                pr: 1,
                mb: -2.5,
              }}
            >
              <a href={pdfMeta?.url} download={pdfMeta?.fileName}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CloudDownloadOutlinedIcon sx={{ cursor: "pointer" }} />
                  <span>Download</span>
                </Box>
              </a>
            </Box>}
          </Grid>
        </Box>
         {pdfMeta?.url ? (
            <Box
              height="calc(100vh - 190px)"
              position={"relative"}
              overflow={"hidden"}
            >
              {isLoading1 && (
                <Box
                  sx={loaderOverlayStyle}>
                  <CircularProgress disableShrink color="primary" />
                </Box>
              )}
              <iframe
                src={`${pdfMeta?.url}#toolbar=0&navpanes=0&scrollbar=0`}
                title="Tax Report Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 4,
                }}
              ></iframe>
            </Box>
          ) : (
            <Box
              style={messageBoxStyle}
            >
              <Typography sx={{ color: "grey.500", fontWeight: "bold" }}>
              {""}
              </Typography>
              {isLoading1 && (
                <Box
                  sx={loaderOverlayStyle}>
                  <CircularProgress disableShrink color="primary" />
                </Box>
              )}
            </Box>
          )}
      </Box>
  );
};
const loaderOverlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const messageBoxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "calc(100vh - 195px)",
    position: "relative",
    border: "1px solid #ddd",
    borderRadius: 5,
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
    padding: 2,
    mx: 2, 
};
export default MyTaxReport;
